import { Api } from 'api';
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { Select, Control, DatePicker, Footer } from '../../../components';
import { useYupValidationResolver } from 'helpers/form';

import {
  SelectWrapper,
  InfoText,
  Loader,
  Container,
  Footer as FooterStyled,
  DatePickerStyled,
} from './AdminStyles';
import { ValidationSchema } from './ValidationSchema';
import { dateToFormat } from 'helpers/formatters/date';
import { showSuccess } from 'helpers/showSuccess';
import { Separator } from './components/Separator';
import { endOfMonth } from 'date-fns';
// import { UpdateDefaultLagDate } from './components/UpdateDefaultLagDate';
// import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { useHistory } from 'react-router-dom';

type Props = {};

interface IFormInputs {
  valueType: number;
  defaultLagDate?: string;
}

export const Admin: React.FC<Props> = () => {
  const resolver = useYupValidationResolver(ValidationSchema);
  const history = useHistory();

  // const [showUpdateDefaultLagDateModal, setShowUpdateDefaultLagDateModal] =
  useState(false);

  // const [hasDefaultLagDate, setHasDefaultLagDate] = useState(false);

  const { data: valueTypesData } = Api.lookup.GetValueTypes();

  const { data: adminSettingsData, isLoading: isLoadingAdminSettings } =
    Api.admin.GetAdminSettings();

  const {
    mutate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
  } = Api.admin.UpdateAdminSettings();

  // const { data: benchmarks } = Api.benchmarks.GetBenchmarks();

  const [isSaving, setIsSaving] = useState(false);

  // const [isUpdatingLagDate, setIsUpdatingLagDate] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty: formDirty /* dirtyFields */ },
    reset,
    resetField,
  } = useForm<IFormInputs>({
    resolver,
    defaultValues: {
      valueType: undefined,
      defaultLagDate: undefined,
    },
  });

  useEffect(() => {
    if (!isLoadingAdminSettings && adminSettingsData) {
      reset({
        valueType: adminSettingsData.valueType.id,
        defaultLagDate: adminSettingsData.defaultLagDate ?? undefined,
      });

      // setHasDefaultLagDate(adminSettingsData.defaultLagDate !== undefined);
    }
  }, [isLoadingAdminSettings, reset]);

  useEffect(() => {
    if (isSuccessUpdate) {
      showSuccess('Your changes have been saved.');
      history.push('/');
      setIsSaving(false);
    }

    if (isErrorUpdate) {
      setIsSaving(false);
      // setIsUpdatingLagDate(false);
    }
  }, [isSuccessUpdate, isErrorUpdate, history]);

  const onSubmit = (value: IFormInputs) => {
    setIsSaving(true);
    mutate({
      valueTypeId: value.valueType,
      defaultLagDate:
        value.defaultLagDate !== undefined
          ? endOfMonth(new Date(value.defaultLagDate)).toDateString()
          : null,
    });
  };

  const discardChanges = () => {
    resetField('valueType');
    resetField('defaultLagDate');
  };

  return (
    <>
      {!isLoadingAdminSettings ? (
        <>
          <Container>
            <div>
              <SelectWrapper>
                <h6>Value Type</h6>
                <Control>
                  <Controller
                    control={control}
                    name={`valueType`}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <Select
                          options={valueTypesData || []}
                          onChange={(val) => onChange(val.value)}
                          value={[...(valueTypesData || [])].filter(
                            (option: any) => option.value === value
                          )}
                          errorMessage={
                            errors[`valueType`] && errors[`valueType`].message
                          }
                          style={{ width: 250 }}
                        />
                      );
                    }}
                  />
                </Control>
              </SelectWrapper>
              <InfoText>
                {adminSettingsData?.lastUpdated &&
                  `Last saved by ${
                    adminSettingsData?.lastUpdatedBy?.fullName
                  } on ${dateToFormat(
                    new Date(adminSettingsData?.lastUpdated)
                  )}.`}
              </InfoText>
            </div>
            <Separator />
            <div>
              <h6>Default Lag Date</h6>
              <p>
                Lagged benchmarks will default to the lag date set here. When
                saving a benchmark, you will also be able to set a custom lag
                date. Lag dates can only be a month end day.
              </p>
              <Control>
                <Controller
                  control={control}
                  name='defaultLagDate'
                  render={({ field: { onChange, value } }) => {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                        }}
                      >
                        <DatePickerStyled>
                          <DatePicker
                            testid='lag-date'
                            style={{
                              width: 150,
                            }}
                            onChange={(date) => {
                              if (date instanceof Date) {
                                onChange(endOfMonth(date));
                              }
                            }}
                            selected={
                              value !== undefined
                                ? endOfMonth(new Date(value))
                                : undefined
                            }
                            dateFormat='MMMM dd yyyy'
                            showMonthYearPicker={true}
                            showMonthPicker={false}
                            showToday={false}
                            isClearable={true}
                            placeholderText='Not Set'
                          />
                        </DatePickerStyled>
                        <p
                          style={{
                            paddingLeft: 10,
                            paddingTop: 5,
                            cursor: 'pointer',
                            color: '#00aee0',
                            fontSize: 12,
                          }}
                          onClick={() => {
                            onChange(undefined);
                          }}
                        >
                          Reset
                        </p>
                      </div>
                    );
                  }}
                />
              </Control>
            </div>
          </Container>{' '}
          <FooterStyled>
            <Footer
              style={{ width: '100%' }}
              showEditButtons
              showReturnHome={false}
              discardText={'Cancel'}
              onSave={() => {
                setIsSaving(true);

                // if (hasDefaultLagDate && dirtyFields?.defaultLagDate) {
                //   setShowUpdateDefaultLagDateModal(true);
                // } else {
                handleSubmit(onSubmit)();
                // }
              }}
              onDiscardChanges={() => {
                setIsSaving(false);
                discardChanges();
              }}
              isDiscardChangesDisabled={!formDirty}
              isSaveDisabled={!formDirty}
              isLoading={isSaving}
            />
          </FooterStyled>
          {/* {hasDefaultLagDate && (
            <ConfirmModal
              testid='updateDefaultLagDateModal'
              show={showUpdateDefaultLagDateModal}
              onClose={() => {
                setIsSaving(false);
                setIsUpdatingLagDate(false);
                setShowUpdateDefaultLagDateModal(false);
                discardChanges();
              }}
              title={'Update Default Lag Date'}
              size={'lg'}
              onConfirm={() => {
                setIsUpdatingLagDate(true);
                handleSubmit(onSubmit)();
                setShowUpdateDefaultLagDateModal(false);
              }}
              confirmLabel={'Yes'}
              isLoading={isUpdatingLagDate}
            >
              <UpdateDefaultLagDate rows={benchmarks || []} />
            </ConfirmModal>
          )} */}
        </>
      ) : (
        <Loader>
          <Spinner animation='border' variant='primary' />
        </Loader>
      )}
    </>
  );
};
